import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import { Typography, useTheme, Snackbar, Alert } from "@mui/material";
import { ReactComponent as SearchIcon } from "../../../assets/icons/dashboard/user/searchIcon.svg";
import AddButton from "../../../components/AddButton";
import TableLayout from "../../../components/TableLayout";
import api from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext";

export default function User() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { user } = useAuth();

  // ✅ Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const canCreate = user?.permissions?.user?.create;
  const canEdit = user?.permissions?.user?.edit;
  const canDelete = user?.permissions?.user?.delete;

  const navigateToAddUser = () => {
    navigate("/user-list/add-user");
  };

  const allColumns = [
    { key: "UserName", label: "Username" },
    { key: "EmailID", label: "Email" },
    { key: "UserRole", label: "Role" },
  ];

  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map((c) => c.key)
  );

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get("/user");
        if (res.data.success) {
          const data = res.data.data || [];

          const mappedRows = data.map((item) => ({
            id: item.UserID,
            UserName: item.UserName,
            EmailID: item.EmailID,
            UserRole: item.RoleName,
            Password: item.Password,
          }));

          setRows(mappedRows);
        }
      } catch (error) {
        console.error("Error fetching user: ", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/user/${id}`);
      setRows((prev) => prev.filter((row) => row.id !== id));

      // ✅ Show success snackbar
      setSnackbar({
        open: true,
        message: "User deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Delete failed: ", error.message);

      // ❌ Show error snackbar
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to delete user",
        severity: "error",
      });
    }
  };

  const filteredRows = rows.filter((row) => {
    const name = row.UserName ? row.UserName.toString() : "";
    const term = searchTerm.trim().toLowerCase();

    return name.toLowerCase().includes(term);
  });

  return (
    <div>
      <div
        className="flex items-center justify-between"
        style={{ padding: "18px" }}
      >
        <div className="font-inter text-[18px] font-semibold !pl-3">Users</div>
        <div className="flex gap-5 items-center">
          <Box className="relative w-[246px]">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by Username"
              className="text-[14px] placeholder:text-[11px] border rounded-[5px] pl-10 pr-2 p-2 h-[40px] w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>
          {canCreate && (
            <AddButton label="Add User" onClick={navigateToAddUser} />
          )}
        </div>
      </div>
      <div className="px-5">
        <TableLayout
          columns={allColumns}
          visibleColumns={visibleColumns}
          rows={filteredRows}
          loading={loading}
          onEdit={
            canEdit ? (id) => navigate(`/user-list/edit-user/${id}`) : null
          }
          onDelete={canDelete ? handleDelete : null}
        />
      </div>

      {/* ✅ Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
